import { Grid, Paper, Typography, Box, LinearProgress } from "@mui/material";
import SubBudget from "./SubBudget";
import { useSelector } from "react-redux";
import { BudgetBucketNodeItem, BudgetItem, BudgetType, BucketNodeSnapshotResponse, CurrentUser, DateTime, FamilyBucketPluralLabel, SortBucketNodesHierarchically } from "../../types";

const Budget = (props: any) => {
	let item: BudgetItem = props.Item;
	const activeDate: DateTime = props.CurrentDate ?? new DateTime();
	const user: CurrentUser = useSelector((state: any) => state.userData.value);
	const bucketNodes: BucketNodeSnapshotResponse | undefined = useSelector((state: any) => state.bucketNodes.value);
	const isIncomeBudget = item.budgetType === BudgetType.Income;
	const isCombinedBudget = item.budgetType === BudgetType.Combined;
	const incomeTotal = item.variant?.incomeAmount ?? 0;
	const incomeSpent = item.incomeAmountGained ?? 0;
	const incomeRemaining = item.incomeAmountRemaining ?? 0;
	const mainTotal = isIncomeBudget ? incomeTotal : item.variant?.amount ?? 0;
	const mainSpent = isIncomeBudget ? incomeSpent : item.amountSpent ?? 0;
	const mainRemaining = isIncomeBudget ? incomeRemaining : item.amountRemaining ?? 0;
	const bucketDefinitionSortOrder = new Map(
		(user.bucketDefinitions?.definitions ?? []).map((definition, index) => [
			definition.bucketTypeKey.toLowerCase(),
			definition.sortOrder ?? index,
		] as const)
	);
	const activeBudgetBucketGroups = Object.values(
		(item.bucketNodes ?? [])
			.filter(bucketNode => IsActiveTimeframe(bucketNode, activeDate))
			.reduce((result, bucketNode) => {
				const key = bucketNode.bucketTypeKey.toLowerCase();
				if (!result[key]) {
					result[key] = {
						bucketTypeKey: bucketNode.bucketTypeKey,
						items: [] as BudgetBucketNodeItem[]
					};
				}
				result[key].items.push(bucketNode);
				return result;
			}, (user.bucketDefinitions?.definitions ?? [])
				.filter(definition => definition.isEnabled && definition.requiresBudgetMapping)
				.reduce((result, definition) => {
					const key = definition.bucketTypeKey.toLowerCase();
					if (!result[key]) {
						result[key] = {
							bucketTypeKey: definition.bucketTypeKey,
							items: [] as BudgetBucketNodeItem[]
						};
					}
					return result;
				}, {} as Record<string, { bucketTypeKey: string, items: BudgetBucketNodeItem[] }>)))
		.map(group => ({
			bucketTypeKey: group.bucketTypeKey,
			label: FamilyBucketPluralLabel(user, group.bucketTypeKey, group.bucketTypeKey),
			items: SortBucketNodesHierarchically(group.items, bucketNodes?.relations)
		}))
		.sort((a, b) => {
			const first = bucketDefinitionSortOrder.get(a.bucketTypeKey.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
			const second = bucketDefinitionSortOrder.get(b.bucketTypeKey.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
			return first - second || a.label.localeCompare(b.label);
		});

	const bucketSummary = activeBudgetBucketGroups
		.map(group => `${group.label}: ${group.items.length > 0 ? group.items.map(bucketNode => bucketNode.displayName).join(", ") : "None"}`)
		.join(" | ");
	const displaySummary = bucketSummary.length < 40 ? bucketSummary : `${bucketSummary.substring(0, 37)}...`;

	let color = item.color;
	let bg = 'white';
	if (item.variant.startDate.dayDiff(new DateTime()) > 0) {
		color += '88';
		bg = 'light'
	}

	return (
		<Grid item xs={12}>
			<Paper elevation={2} sx={{ mb: 2, borderRadius: 3, p: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', color: color, background: bg }} onClick={props.OnClick}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
					<Typography variant="h6" sx={{ fontWeight: 600 }}>{item.description}</Typography>
					<Typography variant="body2">{displaySummary}</Typography>
				</Box>
				<Typography variant="body2" sx={{ mb: 1 }}>{item.variant.startDate.toString()} to {item.endDate?.addDays(-1).toString()}</Typography>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
					<Typography variant="body2">{`${mainSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })} of ${mainTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}`}</Typography>
					<Typography variant="body2">{`${mainRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${mainRemaining >= 0 ? "left" : "over"}`}</Typography>
				</Box>
				<LinearProgress variant="determinate" value={mainTotal > 0 ? Math.min(100, 100 * mainSpent / mainTotal) : 0} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
				{isCombinedBudget && (
					<>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
							<Typography variant="body2">{`${incomeSpent.toLocaleString(undefined, { minimumFractionDigits: 2 })} of ${incomeTotal.toLocaleString(undefined, { minimumFractionDigits: 2 })} income`}</Typography>
							<Typography variant="body2">{`${incomeRemaining.toLocaleString(undefined, { minimumFractionDigits: 2 })} ${incomeRemaining >= 0 ? "left" : "over"}`}</Typography>
						</Box>
						<LinearProgress variant="determinate" value={incomeTotal > 0 ? Math.min(100, 100 * incomeSpent / incomeTotal) : 0} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
					</>
				)}
				{item.subBudgets && item.subBudgets.length > 0 && (
					<Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid #e0e0e0' }}>
						{item.subBudgets.map((sub) => (
							<SubBudget
								Item={sub}
								Type={item.budgetType ?? BudgetType.Expense}
								OnClick={props.OnClick}
								key={`subbudget${item.budgetId}-${sub.subBudgetId}`} />
						))}
					</Box>
				)}
			</Paper>
		</Grid>
	);
}

function IsActiveTimeframe(item: { startDate?: DateTime | null, endDate?: DateTime | null }, activeDate: DateTime) {
	return (!item.startDate || item.startDate.startOfDay().Value <= activeDate.startOfDay().Value)
		&& (!item.endDate || item.endDate.addDays(1).startOfDay().Value > activeDate.startOfDay().Value);
}

export default Budget;